import React, { useEffect, useMemo, useState } from 'react';
import { sectionsAPI } from '../../services/sectionsAPI';
import { formatAPIError, validateEmail, validateName } from '../../utils/validation';
import './ProfileReferencesTab.css';

const SUB_TABS = ['Search', 'Approved', 'Received', 'Sent'];

const ProfileReferencesTab = () => {
  const [subTab, setSubTab] = useState('Search');

  const [loading, setLoading] = useState(true);
  const [employmentId, setEmploymentId] = useState('');

  const [references, setReferences] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Search UI
  const [searchValue, setSearchValue] = useState('');

  // Send via Email modal
  const [sendOpen, setSendOpen] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendForm, setSendForm] = useState({
    name: '',
    email: '',
    message: '', // UI-only for now; backend does not store it
  });
  const [sendErrors, setSendErrors] = useState({});

  const resetFlash = () => {
    setError('');
    setSuccess('');
  };

  const loadEmploymentId = async () => {
    const res = await sectionsAPI.employment.getAll();
    const list = Array.isArray(res.data) ? res.data : [];
    const current = list[0]; // newest first (backend orderBy desc)
    if (!current?.id) throw new Error('No employment found. Please add employment first.');
    setEmploymentId(current.id);
    return current.id;
  };

  const loadReferences = async (empId) => {
    const res = await sectionsAPI.reference.getAll(empId);
    setReferences(Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        resetFlash();
        const empId = await loadEmploymentId();
        await loadReferences(empId);
      } catch (e) {
        setError(e?.message || formatAPIError(e));
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Categorize by promptStatus (backend field)
  const sentRefs = useMemo(() => {
    // Candidate "sent requests" are the references created under their employment
    return references;
  }, [references]);

  const approvedRefs = useMemo(() => {
    // Backend has promptStatus; treat COMPLETED as approved/finished reference
    return references.filter((r) => (r?.promptStatus || '').toUpperCase() === 'COMPLETED');
  }, [references]);

  const normalize = (str = '') =>
  str
    .toLowerCase()
    .replace(/\s+/g, ' ')   // normalize spaces
    .trim();

const searchedRefs = useMemo(() => {
  const q = normalize(searchValue);
  if (!q) return [];

  return references.filter((r) => {
    const name = normalize(r?.name);
    const email = normalize(r?.email);

    return name.includes(q) || email.includes(q);
  });
}, [references, searchValue]);



  const validateSend = () => {
    const errs = {};
    const n = validateName(sendForm.name);
    if (n) errs.name = n;
    const em = validateEmail(sendForm.email);
    if (em) errs.email = em;
    return errs;
  };

  const handleSendViaEmail = async () => {
    resetFlash();
    const errs = validateSend();
    setSendErrors(errs);
    if (Object.keys(errs).length) return;

    if (!employmentId) {
      setError('Employment not found. Please add employment first.');
      return;
    }

    try {
      setSendLoading(true);

      // Backend requires name + email (message is not supported now)
      await sectionsAPI.reference.add(employmentId, {
        name: sendForm.name.trim(),
        email: sendForm.email.trim(),
      });

      setSuccess('Reference request sent successfully.');
      setSendOpen(false);
      setSendForm({ name: '', email: '', message: '' });

      await loadReferences(employmentId);
      setSubTab('Sent');
    } catch (e) {
      setError(formatAPIError(e));
    } finally {
      setSendLoading(false);
    }
  };

  const handleResend = async (referenceId) => {
    resetFlash();
    try {
      await sectionsAPI.reference.resend(referenceId);
      setSuccess('Reminder sent successfully.');
      await loadReferences(employmentId);
    } catch (e) {
      setError(formatAPIError(e));
    }
  };

  // NOTE: "Received" approve/decline needs backend APIs; placeholder for now.
  // Your current backend reference routes don’t provide a "received requests" list or approve/decline endpoints. [file:2]

  return (
    <div className="prt-wrap">
      <div className="prt-head">
        <div>
          <div className="prt-title">Reference Management</div>
          <div className="prt-subtitle">Manage your professional references and reference requests</div>
        </div>
      </div>

      <div className="prt-tabs">
        {SUB_TABS.map((t) => (
          <button
            key={t}
            className={`prt-tab ${subTab === t ? 'active' : ''}`}
            onClick={() => setSubTab(t)}
            type="button"
          >
            {t}
          </button>
        ))}
      </div>

      {(error || success) && (
        <div className="prt-flash">
          {error && <div className="prt-error">{error}</div>}
          {success && <div className="prt-success">{success}</div>}
        </div>
      )}

      <div className="prt-panel">
        {loading ? (
          <div className="prt-empty">Loading...</div>
        ) : subTab === 'Search' ? (
          <>
            <div className="prt-search-row">
              <div className="prt-search-input-wrap">
                <input
                  className="prt-input prt-search-input"
                  placeholder="Search by name or email..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </div>

              <button className="prt-btn prt-btn-outline" type="button" onClick={() => setSendOpen(true)}>
                <span className="prt-mail">✉</span> Send via Email
              </button>
            </div>

            <div className="prt-body">
              {!searchValue.trim() ? (
                <div className="prt-empty">Search for colleagues to request references</div>
              ) : searchedRefs.length === 0 ? (
                <div className="prt-empty">No results found for "{searchValue}"</div>
              ) : (
                <div className="prt-list">
                  {searchedRefs.map((r) => (
                    <div className="prt-row" key={r.id}>
                      <div className="prt-row-left">
                        <div className="prt-row-title">{r.name}</div>
                        <div className="prt-row-sub">{r.email}</div>
                      </div>
                      <div className="prt-row-right">
                        <span className="prt-pill prt-pill-pending">{r.promptStatus || 'PENDING'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : subTab === 'Approved' ? (
          <div className="prt-body">
            {!approvedRefs.length ? (
              <div className="prt-empty">No approved references yet.</div>
            ) : (
              <div className="prt-grid">
                {approvedRefs.map((r) => (
                  <div className="prt-card-approved" key={r.id}>
                    <div className="prt-row-title">{r.name}</div>
                    <div className="prt-row-sub">{r.email}</div>
                    <div className="prt-pill prt-pill-approved">Approved</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : subTab === 'Received' ? (
          <div className="prt-body">
            <div className="prt-empty">
              Received requests UI is ready, but backend endpoints for “received reference requests” and approve/decline
              are not available yet.
            </div>

            {/* When backend is ready, this area will render request cards with Approve/Decline buttons. */}
          </div>
        ) : (
          // Sent
          <div className="prt-body">
            {!sentRefs.length ? (
              <div className="prt-empty">No reference requests sent yet.</div>
            ) : (
              <div className="prt-list">
                {sentRefs.map((r) => {
                  const status = (r.promptStatus || 'NOT_STARTED').toUpperCase();
                  const isCompleted = status === 'COMPLETED';

                  return (
                    <div className={`prt-row prt-row-sent ${isCompleted ? 'approved' : ''}`} key={r.id}>
                      <div className="prt-row-left">
                        <div className="prt-row-title">{r.name}</div>
                        <div className="prt-row-sub">{r.email}</div>
                        <div className="prt-row-sub">Status: {status}</div>
                      </div>

                      <div className="prt-row-actions">
                        {!isCompleted && (
                          <button className="prt-btn prt-btn-outline" onClick={() => handleResend(r.id)} type="button">
                            ✉ Remind
                          </button>
                        )}
                        {isCompleted && <span className="prt-pill prt-pill-approved">Approved</span>}
                        {!isCompleted && <span className="prt-pill prt-pill-pending">Pending</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* SEND VIA EMAIL MODAL */}
      {sendOpen && (
        <div className="prt-modal-overlay" role="dialog" aria-modal="true">
          <div className="prt-modal">
            <div className="prt-modal-head">
              <div>
                <div className="prt-modal-title">Send Reference Request via Email</div>
                <div className="prt-modal-sub">
                  Send a reference request to someone not found in the system
                </div>
              </div>
              <button className="prt-modal-close" onClick={() => setSendOpen(false)} type="button">
                ✕
              </button>
            </div>

            <div className="prt-modal-body">
              <div className="prt-field">
                <label className="prt-label">Name *</label>
                <input
                  className={`prt-input ${sendErrors.name ? 'error' : ''}`}
                  value={sendForm.name}
                  onChange={(e) => setSendForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Colleague name"
                />
                {sendErrors.name && <div className="prt-field-error">{sendErrors.name}</div>}
              </div>

              <div className="prt-field">
                <label className="prt-label">Email Address *</label>
                <input
                  className={`prt-input ${sendErrors.email ? 'error' : ''}`}
                  value={sendForm.email}
                  onChange={(e) => setSendForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="colleague@company.com"
                />
                {sendErrors.email && <div className="prt-field-error">{sendErrors.email}</div>}
              </div>

              <div className="prt-field">
                <label className="prt-label">Message (Optional)</label>
                <textarea
                  className="prt-textarea"
                  value={sendForm.message}
                  onChange={(e) => setSendForm((p) => ({ ...p, message: e.target.value }))}
                  placeholder="Add a personal message to your request..."
                />
                <div className="prt-hint">
                  Note: Message is UI-only right now (backend does not store/send custom message yet).
                </div>
              </div>
            </div>

            <div className="prt-modal-foot">
              <button className="prt-btn prt-btn-secondary" onClick={() => setSendOpen(false)} type="button">
                Cancel
              </button>
              <button
                className="prt-btn prt-btn-primary"
                onClick={handleSendViaEmail}
                disabled={sendLoading}
                type="button"
              >
                {sendLoading ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileReferencesTab;
